import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Calendar, MapPin } from 'lucide-react';
import { conquistasAPI } from '@/lib/api';

const timelineData = [
  {
    ano: '2021',
    titulo: 'O Início',
    descricao: 'Tudo começou durante um simples jogo de vôlei entre amigos. Percebemos a necessidade de formalizar nossa equipe e assim nasceu a Associação ACEC.',
    tipo: 'fundacao'
  },
  {
    ano: '2023',
    titulo: '1º Torneio Beneficente',
    descricao: 'Realizamos nosso primeiro torneio beneficente, marcando o início de uma tradição que se repetiria nos anos seguintes.',
    data: '07 de Janeiro',
    tipo: 'evento'
  },
  {
    ano: '2023',
    titulo: 'Torneio Entre Elas 4x4',
    descricao: 'Participação no Torneio Entre Elas em Parauapebas.',
    data: '24 de Outubro',
    local: 'Parauapebas',
    tipo: 'competicao'
  },
  {
    ano: '2024',
    titulo: 'Copa Araguaia',
    descricao: 'Nossa primeira participação na Copa Araguaia em Conceição do Araguaia.',
    data: '23 de Maio',
    local: 'Conceição do Araguaia',
    tipo: 'competicao'
  },
  {
    ano: '2025',
    titulo: 'Campeãs Municipais!',
    descricao: 'Conquista do 1º lugar no 1º Campeonato Municipal de Voleibol Canaense com a equipe ACEC Feminino Adulto A.',
    data: '17 de Maio',
    tipo: 'conquista'
  },
  {
    ano: '2025',
    titulo: 'Campeãs da Superliga Carajás',
    descricao: 'Vitória histórica na Superliga Carajás com a equipe ACEC Feminino Adulto.',
    data: '18 de Agosto',
    local: 'Carajás',
    tipo: 'conquista'
  },
  {
    ano: '2025',
    titulo: 'Torneio Encontro da Amizade',
    descricao: 'Campeãs do Torneio Encontro da Amizade com a equipe ACEC Juvenil Avançado.',
    data: '14 de Setembro',
    tipo: 'conquista'
  },
  {
    ano: '2026',
    titulo: 'Campeonato Mirim 4x4',
    descricao: '1º lugar no 1º Campeonato de Voleibol Mirim 4x4 com a equipe ACEC Feminino I.',
    data: '7 de Fevereiro',
    tipo: 'conquista'
  }
];

export default function Jornada() {
  const [conquistas, setConquistas] = useState([]);

  useEffect(() => {
    const fetchConquistas = async () => {
      try {
        const res = await conquistasAPI.listar();
        setConquistas(res.data);
      } catch (error) {
        console.error('Erro ao carregar conquistas:', error);
      }
    };
    fetchConquistas();
  }, []);

  const getTipoBadge = (tipo) => {
    const badges = {
      fundacao: { color: 'bg-purple-100 text-purple-700', label: 'Fundação' },
      evento: { color: 'bg-blue-100 text-blue-700', label: 'Evento' },
      competicao: { color: 'bg-green-100 text-green-700', label: 'Competição' },
      conquista: { color: 'bg-yellow-100 text-yellow-700', label: 'Conquista' },
    };
    return badges[tipo] || badges.evento;
  };

  return (
    <div data-testid="jornada-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 hero-pattern" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-yellow-400 font-bold uppercase tracking-widest text-sm">
              Desde 2021
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase mt-4">
              Nossa <span className="text-yellow-400">Jornada</span>
            </h1>
            <p className="text-xl text-blue-100 mt-4 max-w-2xl mx-auto">
              Uma história de paixão, dedicação e conquistas no voleibol
            </p>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-700 via-blue-500 to-yellow-400 transform md:-translate-x-1/2" />

            {timelineData.map((item, index) => {
              const badge = getTipoBadge(item.tipo);
              const isLeft = index % 2 === 0;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center mb-12 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-yellow-400 rounded-full border-4 border-white shadow-lg transform -translate-x-1/2 z-10" />

                  {/* Content */}
                  <div className={`ml-20 md:ml-0 md:w-1/2 ${isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className={`bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow ${
                      item.tipo === 'conquista' ? 'border-l-4 border-l-yellow-400' : ''
                    }`}>
                      <div className={`flex items-center gap-3 mb-3 ${isLeft ? 'md:justify-end' : ''}`}>
                        <span className="font-accent text-3xl font-bold text-blue-700">
                          {item.ano}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${badge.color}`}>
                          {badge.label}
                        </span>
                      </div>
                      
                      <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-2">
                        {item.titulo}
                      </h3>
                      
                      <p className="text-slate-600 mb-3">
                        {item.descricao}
                      </p>
                      
                      {(item.data || item.local) && (
                        <div className={`flex items-center gap-4 text-sm text-slate-500 ${isLeft ? 'md:justify-end' : ''}`}>
                          {item.data && (
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {item.data}
                            </span>
                          )}
                          {item.local && (
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {item.local}
                            </span>
                          )}
                        </div>
                      )}

                      {item.tipo === 'conquista' && (
                        <div className={`flex items-center gap-2 mt-3 ${isLeft ? 'md:justify-end' : ''}`}>
                          <Trophy className="w-5 h-5 text-yellow-500" />
                          <span className="text-yellow-600 font-bold text-sm uppercase">
                            Vitória!
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Future */}
      <section className="py-24 bg-slate-900" data-testid="future-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-yellow-400 font-bold uppercase tracking-widest text-sm">
              E Continua...
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mt-4 mb-6">
              O Melhor Ainda <span className="text-yellow-400">Está por Vir</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Nossa jornada está apenas começando. Com dedicação, trabalho em equipe e o apoio da comunidade, continuaremos a escrever essa história de sucesso.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
